import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import Cookies from "js-cookie";
import Modal from "../../components/modals/Modal";
import { useNavigate } from "react-router-dom";
import { ColumnMap, MODAL_FOOTER_BUTTON_CLASS } from "./ColumnMap";
import {
  IoMdCloudUpload,
} from "react-icons/io";
import { FaCloudArrowUp } from "react-icons/fa6";
import { FaSyncAlt } from "react-icons/fa";
import { Check, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { fetchMetadataColumn, recallfunction } from "../../service/Task";
import { fetchExcelDetails } from "../../service/Task";
import translations from "../../components/common/Translations";
import { DeleteOption } from "./DeleteOption";
import DeletePermission from "../../components/suspence/DeletePermission";
import CommonButton from "../../components/common/CommonButton";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import ReactSelector from "../../components/common/CommonReactSelector";
import MonthYearPicker from "../../components/common/MonthYearPicker";
import { getActivityListByProjectId } from "../../service/User";
import { getPermittedActivities } from "../../utils/activityPermissions";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import { components } from 'react-select';
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import { Spinner } from "../../Spinner";
import PagerArrowButton from "../../components/common/PagerArrowButton";
import AlertModal, { useAlertModal } from "../../components/common/AlertModal";

const MAX_UPLOAD_RECORD_COUNT = 50000; // Maximum number of records allowed for upload

// A sheet can be uploaded into a monthly cycle, or with no cycle at all (a manual
// upload). The cycle is picked as a month + year rather than chosen from a list:
// the cycle being uploaded into usually has no tasks yet, so the backend's cycle
// list -- which is what the Export Report screen offers -- would not contain it.
//
// The value must match how the backend stores it ("march-2026"), since the same
// string comes back from GET /api/v1/task/cycles and is echoed into report
// requests.
const toCycleValue = (date) => {
  if (!date) return null;
  const monthName = date.toLocaleDateString("en-US", { month: "long" });
  return `${monthName.toLowerCase()}-${date.getFullYear()}`;
};

// Tasks are only ever uploaded into the current cycle or an upcoming one, so the
// picker floors at the first of the current month -- every earlier month, and any
// month of an earlier year, is unselectable.
const getCurrentMonthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

const ImportCsv = () => {
  const [file, setFile] = useState(null);
  const [modalValue, setModalValue] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [mapopen, setMapOpen] = useState(false);
  const [colHeaders, setcolHeaders] = useState([]);
  const [mandatoryFields, setmandatoryFields] = useState([]);
  const [optionalFields, setoptionalFields] = useState([]);
  const [open, setOpen] = useState(false);
  const context = useContext(AppContext);
  const { theme, langMode, fileid, setFileId, projectId, updateActivityId, activityId, setWorkflowId } = context;
  const isRtl = langMode === "ar";
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [excelDetails, setexcelDetails] = useState([]);
  const [isLoader, setIsLoading] = useState(false)
  const [deleteOption, setDeleteOption] = useState(false);
  const [showprogress, setShowProgress] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [abortController, setAbortController] = useState(null);
  const [activity, setActivity] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isValidatingFile, setIsValidatingFile] = useState(false);
  const minCycleDate = useMemo(() => getCurrentMonthStart(), []);
  // Deliberately starts empty: a cycle-less upload is a manual upload, and that
  // null is what keeps manual tasks out of cycle reports. Defaulting to the
  // current month would silently pull every import into a cycle.
  const [selectedCycleDate, setSelectedCycleDate] = useState(null);
  const { alertProps, showAlert } = useAlertModal();

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };
  const formatFileSize = (size) => {
    if (size < 1024) {
      return size + " bytes";
    } else if (size >= 1024 && size < 1048576) {
      return (size / 1024).toFixed(1) + " KB";
    } else if (size >= 1048576) {
      return (size / 1048576).toFixed(1) + " MB";
    }
  };

  const prepareFile = async (newFile) => {
    if (!newFile) return;
    const controller = new AbortController();
    setAbortController(controller); // Store controller in state for cancellation

    try {
      const ResponseData = await fetchMetadataColumn(activityId);
      const multiMetaData = ResponseData.data;
        if (multiMetaData.header.code === 600) {
          setmandatoryFields(multiMetaData.body.value.mandatoryColumns);
          setoptionalFields(multiMetaData.body.value.otherColumns);
          setFile(newFile);
          setMapOpen(true);
          const reader = new FileReader();
          reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const headers = [];
            const range = XLSX.utils.decode_range(worksheet["!ref"]);
            const firstRow = range.s.r; // Get the starting row number
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cell =
                worksheet[XLSX.utils.encode_cell({ r: firstRow, c: col })];
              const header = cell ? cell.v : `UNKNOWN ${col}`;
              headers.push(header);
            }
            if (controller.signal.aborted) {
              toast.warn(translations[langMode].failedtodelfile);
              resetFileStates(); // Function to reset all states
              return;
            }
            setcolHeaders(headers);
          };
          reader.readAsArrayBuffer(newFile);
          setFile(newFile);
          setFileSize(formatFileSize(newFile.size));
          setUploadModalOpen(false);
      }
    } catch (error) {
      if (controller.signal.aborted) {
        console.warn("Operation canceled:", error.message);
      } else {
        console.error("Error fetching organization data:", error);
        toast.error(translations[langMode].failstofetchorg);
      }
    }
  };
  // Counts the data rows (header row excluded) of the first sheet.
  const readSheetRecordCount = (selectedFile) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(new Uint8Array(e.target.result), {
            type: "array",
          });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          if (!worksheet || !worksheet["!ref"]) {
            resolve(0);
            return;
          }
          const range = XLSX.utils.decode_range(worksheet["!ref"]);
          resolve(Math.max(range.e.r - range.s.r, 0));
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    });

  const validateAndSetPendingFile = async (selectedFile) => {
    if (!selectedFile) return;
    setIsValidatingFile(true);
    try {
      const recordCount = await readSheetRecordCount(selectedFile);
      if (recordCount > MAX_UPLOAD_RECORD_COUNT) {
        setPendingFile(null);
        showAlert({
          variant: "warning",
          title: translations[langMode].uploadlimitexceeded,
          message: translations[langMode].sheetrecordlimitmsg
            .replace("{count}", recordCount.toLocaleString())
            .replace("{limit}", MAX_UPLOAD_RECORD_COUNT.toLocaleString()),
        });
        return;
      }
      setPendingFile(selectedFile);
    } catch (error) {
      console.error("Error reading sheet:", error);
      setPendingFile(null);
      showAlert({
        variant: "error",
        title: translations[langMode].invalidfiletitle,
        message: translations[langMode].unabletoreadsheet,
      });
    } finally {
      setIsValidatingFile(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) validateAndSetPendingFile(selectedFile);
  };

  // Large sheets keep processing on the backend after the upload finishes.
  const showUploadProcessingAlert = () => {
    showAlert({
      variant: "info",
      title: translations[langMode].fileuploadedtitle,
      message: translations[langMode].largedataprocessingmsg,
    });
  };
  const resetFileStates = () => {
    setFile(null);
    setUploadProgress(0);
    setShowProgress(false);
    setAbortController(null);
  };
  const handleUploadClick = () => {
    if (!activityId) {
      toast.warn(translations[langMode].pleaseselectanactivity);
    } else {
      setPendingFile(null);
      setUploadModalOpen(true);
    }
  };
  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setPendingFile(null);
    setIsDraggingFile(false);
    // Don't touch the file/progress state while an upload is still running.
    if (!showprogress) resetFileStates();
  };
  // Back from the mapping modal returns to the "Upload File" step with the
  // already-picked file kept, instead of cancelling the whole import.
  const handleBackToUploadStep = () => {
    setMapOpen(false);
    setShowProgress(false);
    setUploadProgress(0);
    setPendingFile(file);
    setUploadModalOpen(true);
  };
  const openFilePicker = () => {
    const fileInput = document.getElementById("dropzone-file");
    if (fileInput) {
      fileInput.value = null;
      fileInput.click();
    }
  };
  const handleFileDrop = (event) => {
    event.preventDefault();
    setIsDraggingFile(false);
    if (isValidatingFile) return;
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) validateAndSetPendingFile(droppedFile);
  };
  const navigate = useNavigate();
  const handleGoBack = () => {
    const navigationPath =
      Cookies.get("viewMode") === "map" ? "/maptask" : "/task-manage";
    navigate(navigationPath);
    Cookies.remove("viewMode");
  };
  const totalPages = Math.ceil(totalValue / pageSize) - 1;
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalValue;
  const toastShownRef = useRef(false);

  const handlePageSizeChange = (size) => {
    console.log(size);
    setPageSize(size);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const responseData = await fetchExcelDetails(
        activityId,
        currentPage,
        pageSize
      );
      if (responseData.header.code === 600) {
        setexcelDetails(responseData.body.value.taskExcelList);
        setTotalValue(responseData.body.value.totalCount);
        if (!toastShownRef.current) {
          toast.success(translations[langMode].uploadedtasksheet);
          toastShownRef.current = true; // Set ref value after showing the toast
        }
        setIsLoading(false)
        return;
      } else {
        toast.error(translations[langMode].somthingwentwrong);
      }
      setIsLoading(false)
    } catch (error) {
      console.error("Error fetching task form:", error);
      setIsLoading(false)
    }
  };


  const handleChangeActivity = async (selectedValue) => {
    updateActivityId(selectedValue.value)
  }

  useEffect(() => {
    const fetchActivityList = async () => {
      try {
        const data = await getActivityListByProjectId(projectId);
        setActivity(data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    if (projectId) {
      fetchActivityList(); // Trigger the project fetch whenever `userInfo.organization` changes
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [activityId, currentPage, pageSize]);

  useEffect(() => {
    console.log(pageSize);
  }, [pageSize]);

  const handleDeleteOption = (id) => {
    setFileId(id); // Set the file ID before opening the modal
    setDeleteOption(true);
  };
  const handleRecall = (id) => {
    setFileId(id)
    setDeleteModalOpen(true)
  };
  const RecallFunction = async () => {
    try {
      const data = await recallfunction(fileid, langMode)
      setDeleteModalOpen(false)
    } catch (error) {
      console.error("recall task error:", error);
      toast.error(translations[langMode].failedtorecalltask); // Show error toast
    }
  }

  const permittedActivity = useMemo(() => {
    return getPermittedActivities(activity);
  }, [activity]);

  const option = useMemo(() => {
    return (permittedActivity || [])
      .filter(Boolean)
      .map((item) => ({
        value: item.workflowId,
        label: item.workflowName,
      }));
  }, [permittedActivity]);

  const defaultActivityValue = useMemo(() => {
    if (projectId !== null && permittedActivity?.length) {
      if (activityId) {
        const activityIdList = typeof activityId === 'string'
          ? activityId.split(',')
          : Array.isArray(activityId)
            ? activityId.map(id => id.toString())
            : [activityId?.toString()];

        const matched = permittedActivity
          .filter((org) => activityIdList.includes(org.workflowId?.toString()))
          .map((dept) => ({
            value: dept.workflowId,
            label: dept.workflowName,
          }));

        if (matched.length > 0) return matched;
      }

      return [{
        value: permittedActivity[0].workflowId,
        label: permittedActivity[0].workflowName,
      }];
    }
    return [];
  }, [projectId, activityId, permittedActivity]);

  useEffect(() => {
    if (defaultActivityValue && defaultActivityValue.length > 0) {
      const valueString = defaultActivityValue.map(item => item.value).join(',');
      setWorkflowId(valueString);
      updateActivityId(valueString);
      localStorage.setItem("activityId", valueString);
    }
  }, [defaultActivityValue]);
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white">
      <div className="mx-3 mt-3 shrink-0 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:mx-4 sm:px-5">
      <div className="flex items-center gap-3">
        <button
          onClick={handleGoBack}
          className="
                     rounded-lg
                     bg-blue-50
                     text-[#236DB4]
                     hover:bg-blue-100
                     focus:outline-none
                     focus:ring-2
                     focus:ring-[#236DB4]/30
                     transition
                     duration-200
                     ease-in-out
                     flex items-center justify-center
                     min-h-[36px] h-9 w-9
                     "
          aria-label={translations[langMode].gobacklabel}
          data-testid="go-back-button-task-import-csv"
        >
          <HiOutlineArrowLongLeft size={20} />
        </button>
        <div>
          <h4
            className={`text-lg font-bold text-slate-800 leading-tight ${langMode === 'ar' ? 'text-sm' : ''} ${theme === "dark"
              ? "dark-mode  "
              : theme === "high-contrast"
                ? "high-contrast  "
                : ""
              }`}
            data-testid="import-csv-text"
          >
            {translations[langMode].import}
          </h4>
          <p className="mt-0.5 text-xs text-slate-400">
            {translations[langMode].uploadandreviewtasksheets}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex w-full flex-col sm:flex-row sm:flex-wrap sm:items-center justify-between gap-2.5 sm:gap-3">
          <div className="flex gap-3">
          <div className="w-full sm:w-[220px] text-[#00000066]" data-testid="import-csv-workflow-select">
            <ReactSelector
              options={option || []}
              onChange={handleChangeActivity}
              components={Placeholder}
              placeholder={translations[langMode].Activities}
              isClearable={false}
              background={'#EBF5FF'}
              optionbackground={"#EBF5FF"}
              value={defaultActivityValue}
              dataTestId="import-csv-workflow-select-input"
            />
          </div>
          {/* The cycle this sheet's tasks belong to. Deliberately optional:
              leaving it empty uploads them as a manual, cycle-less import,
              which is what keeps them out of the cycle reports. Sits next to
              the activity because the two together scope the upload. */}
          <div className="w-full sm:w-[220px]" data-testid="import-csv-cycle-select">
            <MonthYearPicker
              value={selectedCycleDate}
              onChange={setSelectedCycleDate}
              minDate={minCycleDate}
              placeholder={translations[langMode].selectcycleoptional}
              // Same tint the activity selector above gets, so the two controls
              // in this toolbar stay visually paired.
              background={'#EBF5FF'}
              dataTestId="import-csv-cycle-select-input"
            />
          </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <CommonButton colorIntent="primary"
              onClick={fetchData}
              variant="primary"
              theme={theme}
              disabled={isLoader}
              tooltipText={translations[langMode].refresh}
              tooltipPosition="top"
              wrapperClassName="shrink-0 sm:w-auto"
              className="!h-9 !w-9 sm:!w-auto !p-0 sm:!px-4 !justify-center"
              data-testid="import-csv-refresh-button"
            >
              <FaSyncAlt size={15} className={isLoader ? "animate-spin" : "shrink-0"} />
              <span className="hidden sm:inline whitespace-nowrap">{translations[langMode].refresh}</span>
            </CommonButton>
            <CommonButton colorIntent="primary"
              onClick={handleUploadClick}
              variant="success"
              theme={theme}
              tooltipText={translations[langMode].uploasthexcelfile}
              tooltipPosition="top"
              wrapperClassName="flex-1 sm:flex-none"
              className="!w-full sm:!w-auto !justify-center whitespace-nowrap !text-xs sm:!text-sm !px-3 sm:!px-4"
              data-testid="import-csv-upload-button"
            >
              <IoMdCloudUpload size={18} className="shrink-0" />
              <span className="whitespace-nowrap">{translations[langMode].uploadfile}</span>
            </CommonButton>
          </div>
        </div>
        <div>
          <div className="flex xsm:justify-start justify-center">
            <input
              id="dropzone-file"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept=".csv,.xlsx,.xls"
              disabled={!activityId}
              data-testid="import-csv-file-input"
            />
          </div>
        </div>

      </div>
      </div>
      {uploadModalOpen && !mapopen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[1px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-sheet-modal-title"
          data-testid="import-csv-upload-modal"
        >
          <div className="flex max-h-[90vh] w-full max-w-[672px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h2 id="upload-sheet-modal-title" className="text-base font-semibold text-slate-800">
                  {translations[langMode].importtaskusingsheet}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {translations[langMode].mapyourfilecolumns}
                </p>
              </div>
              <ModalCloseButton
                onClick={closeUploadModal}
                className="border-0 bg-transparent shadow-none hover:border-0"
                ariaLabel={translations[langMode].cancelandclose}
              />
            </div>

            <div className="overflow-y-auto px-6 py-5">
              <ol className="flex items-start" aria-label={translations[langMode].importprogress}>
                {[
                  translations[langMode].uploadfilelabel,
                  (translations[langMode].taskpriority || translations[langMode].priority).trim(),
                  `${translations[langMode].Mandatory} ${translations[langMode].Fields}`,
                  `${translations[langMode].Other} ${translations[langMode].Fields}`,
                ].map((label, index) => (
                  <li
                    key={label}
                    className={`flex min-w-0 items-start ${index < 3 ? "flex-1" : "flex-none"}`}
                  >
                    <div className="flex min-w-[72px] flex-col items-center">
                      <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                        index === 0 ? "bg-[#3F63EA] text-white" : "bg-slate-100 text-slate-400"
                      }`}>
                        {index + 1}
                      </span>
                      <span className={`mt-2 whitespace-nowrap text-center text-xs ${
                        index === 0 ? "font-medium text-slate-700" : "text-slate-400"
                      }`}>
                        {label}
                      </span>
                    </div>
                    {index < 3 && (
                      <span
                        className="mx-1 mt-3.5 h-px min-w-[12px] flex-1 bg-slate-200"
                        aria-hidden="true"
                      />
                    )}
                  </li>
                ))}
              </ol>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-slate-700">
                  {translations[langMode].uploadfilelabel}
                </h3>
                <p className="mt-1 text-xs text-slate-400">
                  {translations[langMode].uploadtasksheetdesc}
                </p>
                <button
                  type="button"
                  onClick={openFilePicker}
                  disabled={isValidatingFile}
                  onDragEnter={(event) => { event.preventDefault(); if (!isValidatingFile) setIsDraggingFile(true); }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={() => setIsDraggingFile(false)}
                  onDrop={handleFileDrop}
                  className={`mt-3 flex h-[200px] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed transition enabled:cursor-pointer disabled:cursor-not-allowed ${
                    isValidatingFile
                      ? "border-slate-200 bg-slate-50 opacity-70"
                      : pendingFile
                      ? "border-emerald-400 bg-emerald-50/60"
                      : isDraggingFile
                      ? "border-[#3F63EA] bg-blue-50"
                      : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
                  }`}
                >
                  <span className={`flex h-12 w-12 items-center justify-center rounded-full ${
                    pendingFile ? "bg-emerald-100 text-emerald-600" : "bg-blue-50 text-[#2563EB]"
                  }`}>
                    {pendingFile ? <Check size={22} strokeWidth={2.2} /> : <FaCloudArrowUp size={23} />}
                  </span>
                  {isValidatingFile ? (
                    <span className="mt-4 text-sm font-semibold text-slate-700">
                      {translations[langMode].Processing}
                    </span>
                  ) : pendingFile ? (
                    <>
                      <span className="mt-4 max-w-full truncate px-6 text-sm font-semibold text-slate-700">
                        {pendingFile.name}
                      </span>
                      <span className="mt-1 text-xs text-slate-400">
                        {formatFileSize(pendingFile.size)} · {translations[langMode].clicktochooseanotherfile}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="mt-4 text-sm font-semibold text-slate-700">
                        {translations[langMode].draganddroptasksheet}
                      </span>
                      <span className="mt-1 text-xs text-slate-400">
                        {translations[langMode].orclicktobrowse}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-[#E5E7EB] px-6 py-4">
              <CommonButton colorIntent="primary"
                variant="outline"
                theme={theme}
                disabled
                className={MODAL_FOOTER_BUTTON_CLASS}
                tooltipText={translations[langMode].gobacktopreviousstep}
                tooltipPosition="top"
                data-testid="import-csv-upload-modal-back-button"
              >
                {translations[langMode].Back}
              </CommonButton>
              <CommonButton colorIntent="primary"
                onClick={() => prepareFile(pendingFile)}
                variant="primary"
                theme={theme}
                disabled={!pendingFile || isValidatingFile}
                className={MODAL_FOOTER_BUTTON_CLASS}
                tooltipText={
                  pendingFile
                    ? translations[langMode].continuetonextstep
                    : translations[langMode].selectsheettoupload
                }
                tooltipPosition="top"
                data-testid="import-csv-upload-modal-next-button"
              >
                {translations[langMode].next}
              </CommonButton>
            </div>
          </div>
        </div>
      )}
      {showprogress && file && (
        <>
          {uploadProgress !== -1 && (
            <div
              className="mx-5 max-w-sm p-4 mt-6 bg-white border border-slate-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700"
              data-testid="import-csv-upload-progress"
            >
              <div className="flex items-center">
                <img
                  className="w-10 h-10"
                  alt="logo"
                  src="https://e7.pngegg.com/pngimages/261/19/png-clipart-computer-file-computer-icons-file-format-scalable-graphics-comma-separated-values-export-icon-blue-angle.png"
                />
                <div className="p-2">
                  <p className="text-xs font-bold text-slate-700 dark:text-slate-400">
                    {file.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      {fileSize}
                    </span>
                    <div className="w-52 bg-slate-100 rounded-full h-1.5  dark:bg-gray-700">
                      <div
                        className="bg-[#236DB4] h-1.5 rounded-full dark:bg-blue-500"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  if (abortController) {
                    abortController.abort(); // Cancel the current operation
                    resetFileStates(); // Reset states
                  }
                }}
                className="text-red-600 hover:text-red-700 mt-2 text-sm font-medium"
                data-testid="import-csv-upload-cancel-button"
              >
                {translations[langMode].cancelupload}
              </button>
            </div>
          )}
        </>
      )}
      <div className={`mx-3 mb-3 mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
            <div className="flex min-h-[56px] shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5">
              <p className="flex items-center gap-3 text-xs font-medium text-slate-500">
                {`${startIndex}-${endIndex} ${translations[langMode].of} ${totalItems} ${translations[langMode].items}`}
              </p>
              <div className="flex items-center gap-3 text-xs font-medium text-slate-500">
                <label htmlFor="pageSize">{translations[langMode].rows}</label>
                <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} />
                <PagerArrowButton
                  direction="prev"
                  isRtl={isRtl}
                  onClick={goToPreviousPage}
                  disabled={currentPage === 0}
                  ariaLabel={translations[langMode]?.previousPage || "Previous page"}
                />
                <span className="min-w-[38px] text-center tabular-nums text-slate-600">
                  {currentPage + 1} / {Math.max(totalPages + 1, 1)}
                </span>
                <PagerArrowButton
                  direction="next"
                  isRtl={isRtl}
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  ariaLabel={translations[langMode]?.nextPage || "Next page"}
                />
              </div>
            </div>
            <div className="min-h-0 w-full flex-1 overflow-auto">
              <table className="min-w-max w-full table-auto text-left text-xs text-slate-600 [&_th]:!px-4 [&_th]:!py-3 [&_td]:!px-4 [&_td]:!py-3 [&_tbody_button]:!h-8 [&_tbody_button]:!min-h-[32px] [&_tbody_button]:!px-3 [&_tbody_button]:!py-0">
                <thead
                  className={`sticky top-0 z-10 whitespace-nowrap border-b border-slate-200 bg-slate-50 text-xs font-normal text-slate-500 dark:text-slate-400 ${theme === "dark"
                    ? "dark-mode  "
                    : theme === "high-contrast"
                      ? "high-contrast  "
                      : ""
                    }`}
                >
                  <tr>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].uploadby}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].uploaddate}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].uploadtime}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].download}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].delete}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].Recall}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].Status}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].uploadedCount}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].movedCount}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].updatedCount}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].notUploadedCount}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].notUploadedLog}
                    </th>
                    <th
                      scope="col"
                      className={`px-6 py-3 uppercase text-xs font-semibold text-slate-500 ${theme === "dark"
                        ? "dark-mode  "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                        }`}
                    >
                      {translations[langMode].movedLog}
                    </th>

                  </tr>
                </thead>
                <tbody className="overflow-x-auto whitespace-nowrap">
                  {isLoader ? <Spinner />
                    : excelDetails && excelDetails.length > 0 ? (
                      excelDetails.map((detail) => (
                        <tr
                          key={detail.id}
                          data-testid="import-csv-excel-row"
                          className={`odd:bg-white gap-8 even:bg-slate-50/60 border-b
                           ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                            }`}
                        >
                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {detail.uploadedByName}
                          </td>
                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {new Date(detail.uploadDate).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </td>
                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {new Date(`1970-01-01T${detail.uploadTime}`).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </td>
                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            <CommonButton colorIntent="primary"
                              variant='primary'
                              tooltipText={translations[langMode].donwloastheuplodedexcel}
                              tooltipPosition="top"
                              className="!h-10 !min-h-[40px] !rounded-xl !px-5"
                              data-testid="download-csv"
                            >
                              {/* The link fills the whole button so the icon, label and padding are all clickable. */}
                              <a
                                href={detail.excelUrl}
                                download
                                className="-mx-5 flex h-full items-center gap-2 px-5"
                              >
                                <Download size={18} />
                                {translations[langMode].download}
                              </a>
                            </CommonButton>
                          </td>

                          <td
                            className={` px-6 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            <CommonButton colorIntent="danger"
                              onClick={() => {
                                handleDeleteOption(detail.id);
                              }}
                              variant="danger"
                              tooltipText={translations[langMode].deleterecordfrom}
                              tooltipPosition="top"
                            >

                              {translations[langMode].delete}
                            </CommonButton>
                          </td>

                          <td
                            className={` px-6 ${theme === "dark"
                                ? "dark-mode  "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                                }`}
                          >
                            {detail.isRecordsDeleted !== true ? (
                              <CommonButton colorIntent="warning"
                                onClick={() => {
                                  handleRecall(detail.id);
                                }}
                                variant="success"
                                tooltipText={translations[langMode].recallassingedtask}
                                tooltipPosition="top"
                              >

                                {translations[langMode].Recall}
                              </CommonButton>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            <span className={`inline-flex h-6 items-center rounded-md border px-2 text-[11px] font-semibold ${
                              detail?.status === true
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-amber-200 bg-amber-50 text-amber-700"
                            }`}>
                              {detail?.status === true
                                ? translations[langMode].completed
                                : translations[langMode].inProgress}
                            </span>
                          </td>
                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {detail?.uploadedCount ?? translations[langMode].notAvailable}
                          </td>  <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {detail?.movedCount ?? translations[langMode].notAvailable}
                          </td>  <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {detail?.updatedCount ?? translations[langMode].notAvailable}
                          </td>
                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {detail?.notUploadedCount ?? translations[langMode].notAvailable}
                          </td>

                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {detail?.notUploadedLog ? (
                              <CommonButton colorIntent="primary"
                                variant='primary'
                                tooltipText={translations[langMode].downloadnotuploadedlog}
                                tooltipPosition="top"
                              >
                                <a
                                  href={detail?.notUploadedLog}
                                  download
                                >
                                  {translations[langMode].download}
                                </a>
                              </CommonButton>) : (
                              <div>{translations[langMode].notAvailable}</div>
                            )}
                          </td>

                          <td
                            className={`px-6 py-4 font-medium text-slate-700 ${theme === "dark"
                              ? "dark-mode  "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {detail?.movedLog ? (
                              <CommonButton colorIntent="primary"
                                variant='primary'
                                tooltipText={translations[langMode].downloadnotmovedlog}
                                tooltipPosition="top"
                              >
                                <a
                                  href={detail?.movedLog}
                                  download
                                >
                                  {translations[langMode].download}
                                </a>
                              </CommonButton>
                            ) : (
                              <div>{translations[langMode].notAvailable}</div>
                            )
                            }

                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={13}
                          className="px-6 py-4 text-center text-slate-400"
                          data-testid="import-csv-no-data-message"
                        >
                          {translations[langMode].nodatavailvle}
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>
      <Modal
        open={open}
        setOpen={setOpen}
        value={modalValue}
        setModalValue={setModalValue}
      />
      {file && (
        <ColumnMap
          fetchData={fetchData}
          showprogress={showprogress}
          setShowProgress={setShowProgress}
          newFile={file}
          mapopen={mapopen}
          setMapOpen={setMapOpen}
          colHeaders={colHeaders}
          optionalFields={optionalFields}
          mandatoryFields={mandatoryFields}
          setModalValue={setModalValue}
          open={open}
          setOpen={setOpen}
          selectedOption={activityId}
          cycle={toCycleValue(selectedCycleDate)}
          setUploadProgress={setUploadProgress}
          uploadProgress={uploadProgress}
          setFile={setFile}
          abortController={abortController}
          setAbortController={setAbortController}
          resetFileStates={resetFileStates}
          onUploadSuccess={showUploadProcessingAlert}
          onBackToUpload={handleBackToUploadStep}
        />
      )}
      <DeleteOption
        deleteOption={deleteOption}
        setDeleteOption={setDeleteOption}
        fileid={fileid}
        fetchData={fetchData}
      />
      {deleteModalOpen && (
        <DeletePermission
          deleteFunction={() => RecallFunction()}
          onClose={handleCloseDeleteModal}
          onDeleteText={translations[langMode].areusurewanttoallassinged}
          confirmTooltipText={translations[langMode].proceedwithrecallaction}
        />
      )}
      <AlertModal {...alertProps} />
    </div>
  );
};

export default ImportCsv;
